import React, { useState } from "react";
import { Container, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import { DISPENSING_LOG_PATHS } from "../../../../Routes/paths";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { ReasonSubmitPayload } from "../../../../common/utilities/Reason/Reason";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { useDispensingLog } from "../api/useDispensingLog";
import {
  GetUserData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import { queryClient } from "../../../../api/queryClient";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import PinVerificationModal from "../../DailyReconsilation/components/PinVerificationModal";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import { DispensingRecord } from "../helpers/dispensing.types";
import { mapDispensingRecord } from "../helpers/dispensing.helper";
import {
  getDispensingLogListColumns,
} from "../helpers/dispensing.constant";

const DispensingLogList: React.FC = () => {
  document.title = "Dispensing Logs | ZENTIXS ORBIT";

  /* ------------------------------------------------------------------------ */
  /* 1. ROUTING, CONTEXT & LOCAL STATE                                        */
  /* ------------------------------------------------------------------------ */
  const navigate = useNavigate();
  const userData = GetUserData();

  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [recordToDelete, setRecordToDelete] = useState<DispensingRecord | null>(
    null,
  );
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<DispensingRecord | null>(
    null,
  );

  // Audit State
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // Global Study Context
  const { selectedStudy: globalStudy } = useGlobalStudy();

  /* ------------------------------------------------------------------------ */
  /* 2. SERVICES & API QUERIES                                                */
  /* ------------------------------------------------------------------------ */
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const { SelectAllQuery, DeleteMutation, verifyPinMutation } =
    useDispensingLog({
      searchText,
      pageNumber: currentPageNumber,
      pageSize: 5,
      studyid: Number(globalStudy?.studyid) || null,
      enableSelectall: true,
    });

  /* ------------------------------------------------------------------------ */
  /* 3. MEMOIZED DATA & COMPUTATIONS                                          */
  /* ------------------------------------------------------------------------ */
  const {
    data: queryResponse,
    isLoading,
    isFetching,
    isError,
  } = SelectAllQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: DispensingRecord[] = (queryResponse?.data ?? []).map(mapDispensingRecord);

  /* ------------------------------------------------------------------------ */
  /* 4. EVENT HANDLERS & MODAL ACTIONS                                        */
  /* ------------------------------------------------------------------------ */
  const onAuditClick = () => {
    const payload = {
      modulename: "Dispensing",
      studyid: globalStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Dispensing_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPageNumber(1);
  };

  const handleDeleteClick = (record: DispensingRecord) => {
    setRecordToDelete(record);
    setDeleteModal(true);
  };

  const handleVerifyClick = (record: DispensingRecord) => {
    setSelectedRecord(record);
    setVerifyModal(true);
  };

  const onVerifyPinSubmit = (password: string) => {
    if (!selectedRecord) return;

    const payload = {
      verifypin: password,
      dispensingid: Number(selectedRecord.dispensingid),
      studyid: Number(selectedRecord.studyid),
      verifiedby: Number(userData?.userid),
    };

    verifyPinMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["dispensing-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message);
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: any) => {
        showToastMessage(
          "error",
          error?.response?.data?.message || "Invalid password",
        );
      },
    });
  };

  const getRowActions = (row: DispensingRecord): any => {
    const isVerified = row?.isverified;

    const actions: any = {};

    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = (r: any) =>
        navigate(
          DISPENSING_LOG_PATHS.EDIT.replace(":id", r.dispensingid.toString()),
        );
      actions.onDelete = (r: any) => handleDeleteClick(r);
    }
    return actions;
  };

  const handleDeleteConfirm = (payload: ReasonSubmitPayload) => {
    const deletePayload = {
      dispensingid: payload.id,
      updatereason: payload.reason,
    };

    DeleteMutation.mutate(deletePayload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Record deleted successfully",
        );
        queryClient.invalidateQueries({ queryKey: ["dispensing-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        setCurrentPageNumber(1);
        setDeleteModal(false);
        setShowDeleteReason(false);
        setRecordToDelete(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || "Delete failed");
      },
    });
  };

  const columns: any = getDispensingLogListColumns((dispensingId: number) =>
    navigate(
      DISPENSING_LOG_PATHS.VIEW.replace(":id", dispensingId.toString())
    )
  );

  /* ------------------------------------------------------------------------ */
  /* 5. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="dispensing" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <h5 className={`mb-1 ${styles.pageHeaderTitle}`}>
                  Dispensing Logs
                </h5>
                <p className={styles.pageHeaderSubtitle}>
                  Track and manage Investigational Product (IP) dispensing records.
                </p>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.DISPENSING_LOG}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                  size="sm"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                {/* <CommonButton
                  color="primary"
                  outline
                  className="px-2 fw-600"
                  size="sm"
                  module={PERMISSION_MODULES.DISPENSING_LOG}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate(DISPENSING_LOG_PATHS.LINE_CLEARANCE)}
                >
                  <i className="ri-checkbox-circle-line me-1" /> Line Clearance
                </CommonButton> */}
                <CommonButton
                  color="primary"
                  outline
                  className="px-2 fw-600"
                  size="sm"
                  module={PERMISSION_MODULES.DISPENSING_LOG}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate(DISPENSING_LOG_PATHS.ADD)}
                >
                  <i className="ri-add-line me-1" /> New Dispensing
                </CommonButton>
              </div>
            </div>

            {/* ── Table & Placeholder ── */}         <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <h2>
                  <i className="ri-file-list-3-line me-2 text-primary fs-18" />
                  Dispensing Logs
                </h2>

                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              <div className="px-4">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.DISPENSING_LOG}
                  columns={columns}
                  data={data}
                  loading={isLoading || isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={data[0]?.total_count || 0}
                  onPageChange={setCurrentPageNumber}
                  actions={(row) => getRowActions(row)}
                />
              </div>
            </div>
          </>
        )}

        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}

        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            recordId={recordToDelete?.dispensingid}
            recordName={recordToDelete?.logid}
            toggle={() => setDeleteModal(!deleteModal)}
            onDeleteClick={handleDeleteConfirm}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={() => setShowDeleteReason((p) => !p)}
          />
        )}

        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={onVerifyPinSubmit}
          isLoading={verifyPinMutation.isPending}
          title="Verify Dispensing Log"
          description={
            <>
              Please enter your password to verify Dispensing Log ID : <strong>{selectedRecord?.logid}</strong>
            </>
          }
          isInvalid={verifyPinMutation.isError}
        />
      </Container>
    </div>
  );
};

export default DispensingLogList;
